import React from "react";
import { styled } from "styled-components";
import { CapabilityLadder, ladderSummary, rungLabel } from "./capability";
import { glyphChar, severityStyle } from "./severity";
import { tokens } from "./tokens";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";

/**
 * One tractor's position on the capability ladder, in a cell.
 *
 * Three encodings of the same fact, because each answers a different question at
 * a different distance:
 *
 * - **Pips** answer "how far up, compared to the rows above and below" without
 *   reading. This is the scan, and it is why the ladder is worth collapsing at
 *   all — four columns of enum labels cannot be compared down a fleet.
 * - **Label + glyph** answer "which rung, exactly". The glyph is the option's
 *   own: a check for an outright approval, a half for one that is scoped or
 *   conditional. So `◐ Auto testing` reads as "autonomous testing, with a
 *   catch" and the catch itself is a hover away.
 * - **The ragged marker** answers the question nobody thought to ask — the
 *   tractor is cleared for something higher than a rung it has been refused.
 *
 * Never colour alone: the pip row is shape-and-fill, the rung carries a glyph,
 * and the tooltip spells all of it out in words. The fill is green rather than
 * blue because blue is the interaction colour everywhere else here — a held
 * rung is a fact, not a thing you are pointing at — and green is what the ✓
 * chips beside it already use.
 */
interface CapabilityCellProps {
  ladder: CapabilityLadder;
}

const CapabilityCell: React.FC<CapabilityCellProps> = ({ ladder }) => {
  if (ladder.unknown) return <Unknown>—</Unknown>;

  const top = ladder.top;
  const style = severityStyle(top?.option?.severity ?? "neutral");
  const height = top ? ladder.rungs.indexOf(top) + 1 : 0;

  // A Radix tooltip rather than a native `title`: the summary is one line per
  // rung and the browser tooltip neither wraps on purpose nor opens for
  // keyboard focus. The provider is mounted by the table around every cell.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Wrap aria-label={ladderSummary(ladder)}>
          <Pips aria-hidden>
            {ladder.rungs.map((rung, i) => (
              <Pip
                key={rung.field.name}
                $state={i < height ? "filled" : rung.state}
              />
            ))}
          </Pips>
          {top ? (
            <Rung style={{ color: style.color }}>
              <Glyph aria-hidden>{glyphChar(top.option?.glyph)}</Glyph>
              <RungName>{rungLabel(top.field)}</RungName>
            </Rung>
          ) : (
            // Every rung refused, off-ladder or unset. "None" is a real answer
            // and reads differently from the em dash for "nothing recorded".
            <Rung style={{ color: tokens.slate }}>
              <Glyph aria-hidden>{glyphChar("cross")}</Glyph>
              <RungName>None</RungName>
            </Rung>
          )}
          {ladder.ragged.length > 0 && <Ragged aria-hidden>!</Ragged>}
        </Wrap>
      </TooltipTrigger>
      <TooltipContent className="whitespace-pre-line">
        {ladderSummary(ladder)}
      </TooltipContent>
    </Tooltip>
  );
};

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  overflow: hidden;
`;

const Pips = styled.span`
  display: inline-flex;
  gap: 2px;
  flex-shrink: 0;
`;

/**
 * One rung. Filled up to the tractor's high-water mark; above it, the pip shows
 * what that rung actually says — a refusal reads differently from a blank, and
 * an off-ladder rung is drawn hollow because it is not a gap to be closed.
 *
 * Every pip keeps a visible outline, including the empty ones. Without it the
 * unfilled slots vanished against the row and a two-rung tractor looked like a
 * two-rung *ladder* — the position was unreadable because the scale had
 * disappeared. The whole value of the pips is the comparison, and you cannot
 * compare against a scale you cannot see.
 */
const Pip = styled.span<{ $state: string }>`
  width: 7px;
  height: 14px;
  border-radius: 2px;
  box-sizing: border-box;
  border: 1px ${(p) => (p.$state === "off" ? "dashed" : "solid")}
    ${(p) =>
      p.$state === "filled"
        ? tokens.readyText
        : p.$state === "denied"
          ? "rgba(229,72,77,0.55)"
          : p.$state === "conditional"
            ? "rgba(255,150,0,0.65)"
            : tokens.toggleOff};
  background: ${(p) =>
    p.$state === "filled"
      ? tokens.readyText
      : p.$state === "denied"
        ? "rgba(229,72,77,0.22)"
        : p.$state === "conditional"
          ? "rgba(255,150,0,0.30)"
          : "transparent"};
`;

const Rung = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
`;

const Glyph = styled.span`
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;
`;

const RungName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The ladder has a hole under the tractor's own high-water mark. */
const Ragged = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: ${tokens.majorText};
  background: rgba(255, 150, 0, 0.12);
  border: 1px solid rgba(255, 150, 0, 0.4);
  border-radius: 4px;
  padding: 0 5px;
`;

const Unknown = styled.span`
  font-size: 13px;
  color: ${tokens.slate};
`;

export default CapabilityCell;
